import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import './ECIScraper.css';

const ECIScraper = () => {
  const [status, setStatus] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const data = await api.getScraperStatus();
      setStatus(data);
    } catch (error) {
      console.error('Error loading status:', error);
      setStatus({
        totalCandidates: 0,
        candidatesWithDetails: 0,
        candidatesWithoutDetails: 0,
        totalResults: 0
      });
    }
  };

  const startScraping = async () => {
    const confirmed = window.confirm(t('scraper.confirmMessage'));

    if (!confirmed) return;

    setScraping(true);
    setResult(null);
    setProgress(0);
    setCurrentStep(t('scraper.initializing'));

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 1000);

      const data = await api.scrapeECIAffidavits();
      
      clearInterval(progressInterval);
      setProgress(100);
      setCurrentStep(t('scraper.completed'));
      
      setResult(data);
      await loadStatus();
    } catch (error) {
      setResult({ 
        success: false, 
        errorMessage: error.message || t('scraper.failedMessage')
      });
    } finally {
      setScraping(false);
      setTimeout(() => {
        setProgress(0);
        setCurrentStep('');
      }, 3000);
    }
  };

  const statusCards = status ? [
    { 
      label: t('scraper.totalCandidates'), 
      value: status.totalCandidates || 0, 
      icon: 'users',
      color: '#667eea'
    },
    { 
      label: t('scraper.withDetails'), 
      value: status.candidatesWithDetails || 0, 
      icon: 'check-circle',
      color: '#10b981'
    },
    { 
      label: t('scraper.withoutDetails'), 
      value: status.candidatesWithoutDetails || 0, 
      icon: 'exclamation-circle',
      color: '#ef4444'
    },
    { 
      label: t('scraper.totalResults'), 
      value: status.totalResults || 0, 
      icon: 'chart-line',
      color: '#f59e0b'
    },
    { 
      label: t('scraper.latestRound'), 
      value: status.latestRound || t('scraper.na'), 
      icon: 'clock',
      color: '#8b5cf6'
    },
    { 
      label: t('scraper.lastUpdate'), 
      value: status.lastUpdate 
        ? new Date(status.lastUpdate).toLocaleString() 
        : t('scraper.never'), 
      icon: 'calendar',
      color: '#06b6d4'
    }
  ] : [];

  return (
    <div className="eci-scraper-container">
      {/* Header */}
      <div className="scraper-header">
        <h2>
          <i className="fas fa-robot"></i>
          {t('scraper.title')}
        </h2>
        <p>
          {t('scraper.subtitle')}{' '}
          <a 
            href="https://affidavit.eci.gov.in/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            {t('scraper.eciPortal')}
            <i className="fas fa-external-link-alt"></i>
          </a>
        </p>
      </div>

      {/* Info Box */}
      <div className="info-box">
        <h3>
          <i className="fas fa-clipboard-list"></i>
          {t('scraper.whatWillBeScraped')}
        </h3>
        <ul>
          <li><i className="fas fa-check"></i> {t('scraper.item1')}</li>
          <li><i className="fas fa-check"></i> {t('scraper.item2')}</li>
          <li><i className="fas fa-check"></i> {t('scraper.item3')}</li>
          <li><i className="fas fa-check"></i> {t('scraper.item4')}</li>
          <li><i className="fas fa-check"></i> {t('scraper.item5')}</li>
        </ul>
      </div>

      {/* Progress Bar */}
      {scraping && (
        <div className="progress-section">
          <div className="progress-header">
            <span className="progress-label">{t('scraper.scrapingInProgress')}</span>
            <span className="progress-percentage">{progress}%</span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          {currentStep && (
            <div className="current-step">
              <i className="fas fa-spinner fa-spin"></i>
              {currentStep}
            </div>
          )}
        </div>
      )}

      {/* Status Cards */}
      {status && (
        <div className="status-section">
          <h3>
            <i className="fas fa-info-circle"></i>
            {t('scraper.currentStatus')}
          </h3>
          <div className="status-grid">
            {statusCards.map((card, idx) => (
              <div 
                key={idx} 
                className="status-card"
                style={{ '--card-color': card.color }}
              >
                <div className="status-icon">
                  <i className={`fas fa-${card.icon}`}></i>
                </div>
                <div className="status-content">
                  <div className="status-label">{card.label}</div>
                  <div className="status-value">{card.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Result Box */}
      {result && (
        <div className={`result-box ${result.success ? 'success' : 'error'}`}>
          <div className="result-header">
            <i className={`fas fa-${result.success ? 'check-circle' : 'exclamation-circle'}`}></i>
            <h3>
              {result.success 
                ? t('scraper.successMessage')
                : t('scraper.failedMessage')}
            </h3>
          </div>
          
          {result.success && (
            <div className="result-grid">
              <div className="result-item">
                <i className="fas fa-clock"></i>
                <div>
                  <span className="result-label">{t('scraper.duration')}</span>
                  <span className="result-value">{result.duration || t('scraper.na')}</span>
                </div>
              </div>
              <div className="result-item">
                <i className="fas fa-map-marked-alt"></i>
                <div>
                  <span className="result-label">{t('scraper.constituencies')}</span>
                  <span className="result-value">{result.constituenciesFound || 0}</span>
                </div>
              </div>
              <div className="result-item">
                <i className="fas fa-download"></i>
                <div>
                  <span className="result-label">{t('scraper.scraped')}</span>
                  <span className="result-value">{result.candidatesScraped || 0}</span>
                </div>
              </div>
              <div className="result-item">
                <i className="fas fa-sync-alt"></i>
                <div>
                  <span className="result-label">{t('scraper.updated')}</span>
                  <span className="result-value">{result.candidatesUpdated || 0}</span>
                </div>
              </div>
            </div>
          )}
          
          {result.errorMessage && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              {result.errorMessage}
            </div>
          )}

          <button 
            className="close-result-btn"
            onClick={() => setResult(null)}
          >
            {t('scraper.close')}
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons">
        <button
          onClick={startScraping}
          disabled={scraping}
          className={`action-btn primary ${scraping ? 'disabled' : ''}`}
        >
          {scraping ? (
            <>
              <div className="spinner-btn"></div>
              {t('scraper.scraping')}
            </>
          ) : (
            <>
              <i className="fas fa-download"></i>
              {t('scraper.startScraping')}
            </>
          )}
        </button>
        
        <button
          onClick={loadStatus}
          disabled={scraping}
          className="action-btn secondary"
        >
          <i className="fas fa-sync-alt"></i>
          {t('scraper.refreshStatus')}
        </button>
      </div>

      {/* Warning Notice */}
      <div className="warning-notice">
        <i className="fas fa-exclamation-triangle"></i>
        <div>
          <strong>{t('scraper.warningTitle')}</strong> {t('scraper.warningMessage')}
        </div>
      </div>
    </div>
  );
};

export default ECIScraper;
